import { MoreOutlined } from '@ant-design/icons'
import { Button, Dropdown, Menu, Popover, Tag, Tooltip, Typography } from 'antd'
import { ColumnFilterItem } from 'antd/lib/table/interface'
import React from 'react'

import { emptyCharacter } from '../../../../components/EmptyChar'
import { buildSearchFilterProps, sortNumericColumn, sortStringColumn } from '../../../../components/tables/utils'
import { formatRating } from '../../../../formatters/format-rating'
import { sortStrings } from '../../../../util/sorters/sort-strings'
import { Issuer, IssuerTier } from '../../models'
import { tiersByInstrument } from '../UpdateIssuerTier/models'

const renderIssuerTiers = (tiers: IssuerTier[]) => {
  if (!tiers.length) {
    return emptyCharacter
  }

  const issuerTiers = tiers?.sort(sortByInstrumentDesc).map(({ issuerId, instrumentCode, tier }) => (
    <p key={`${issuerId}${instrumentCode}${tier}`}>
      {instrumentCode} - {tiersByInstrument[instrumentCode][tier]}
    </p>
  ))

  return (
    <Popover
      content={issuerTiers}
      title={<Typography.Title level={5}>{issuerTiers.length > 1 ? 'Multiple Tiers' : 'Single Tier'}</Typography.Title>}
    >
      <Tag className="issuers-count">
        {tiers
          ?.map(({ instrumentCode, tier }) => `${instrumentCode} - ${tiersByInstrument[instrumentCode][tier]}`)
          .join(' | ')}
      </Tag>
    </Popover>
  )
}

const renderActionsMenu =
  ({
    onUpdate,
    onUpdateIssuerTier,
  }: {
    onUpdate: (issuer: Issuer) => void
    onUpdateIssuerTier: (issuer: Issuer) => void
  }) =>
  (issuer: Issuer) => {
    const menu = (
      <Menu>
        <Menu.Item key="onUpdate" onClick={() => onUpdate(issuer)}>
          Update Issuer
        </Menu.Item>
        <Menu.Item key="onUpdateTier" onClick={() => onUpdateIssuerTier(issuer)}>
          Update Issuer Tier
        </Menu.Item>
      </Menu>
    )

    return (
      <Dropdown placement="bottomRight" overlay={menu}>
        <Button icon={<MoreOutlined />} />
      </Dropdown>
    )
  }

export const buildIssuersTableColumns = ({
  onUpdate,
  onUpdateIssuerTier,
  filters,
}: {
  onUpdate: (issuer: Issuer) => void
  onUpdateIssuerTier: (issuer: Issuer) => void
  filters: {
    shortRatings: ColumnFilterItem[]
    longRatings: ColumnFilterItem[]
  }
}) => [
  {
    title: 'ID',
    dataIndex: 'id',
    sorter: sortNumericColumn({ dataIndex: 'id' }),
    ...buildSearchFilterProps({ dataIndex: 'id' }),
  },
  {
    title: 'Display Name',
    dataIndex: 'shortName',
    sorter: sortStringColumn({ dataIndex: 'shortName' }),
    ...buildSearchFilterProps({
      dataIndex: 'shortName',
      render: (shortName: string, issuer: Issuer) => <Tooltip title={issuer.name}>{shortName}</Tooltip>,
    }),
  },
  {
    title: 'Code',
    dataIndex: 'code',
    sorter: sortStringColumn({ dataIndex: 'code' }),
    ...buildSearchFilterProps({ dataIndex: 'code' }),
  },
  {
    title: 'Long Rating',
    dataIndex: 'longRating',
    filters: filters.longRatings,
    render: formatRating,
    onFilter: (longRating: string, issuer: Issuer) => issuer.longRating === longRating,
  },
  {
    title: 'Short Rating',
    dataIndex: 'shortRating',
    filters: filters.shortRatings,
    render: formatRating,
    onFilter: (shortRating: string, issuer: Issuer) => issuer.shortRating === shortRating,
  },
  {
    title: 'Banking Group',
    dataIndex: 'bankingGroup',
    sorter: sortStringColumn({ dataIndex: 'bankingGroup' }),
    ...buildSearchFilterProps({ dataIndex: 'bankingGroup' }),
  },
  {
    title: 'Tiers',
    dataIndex: 'tiers',
    render: renderIssuerTiers,
  },
  { title: '', render: renderActionsMenu({ onUpdate, onUpdateIssuerTier }) },
]

const sortByInstrumentDesc = (firstTier: IssuerTier, secondTier: IssuerTier) =>
  sortStrings(firstTier.instrumentCode, secondTier.instrumentCode) * -1
